import { useEffect, useState, useRef } from "react";
import { api } from "../lib/api";
import { useTheme } from "../context/ThemeContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Palette, Store, Users as UsersIcon, Gift, Trash2, Plus, Upload, Receipt as RcpIcon, MessageCircle, CheckCircle2, XCircle, Loader2, ExternalLink, Calendar, Copy, Clock } from "lucide-react";

const ALL_PERMS = ["dashboard", "pos", "bookings", "inventory", "customers", "members", "reminders", "calendar", "settings"];
const PERM_LABEL = {
  dashboard: "Dashboard", pos: "POS Kasir", bookings: "Booking", inventory: "Inventaris",
  customers: "Pelanggan", members: "Member", reminders: "WhatsApp", calendar: "Kalender", settings: "Pengaturan"
};
import { toast } from "sonner";

export default function Settings() {
  const { settings, reload } = useTheme();
  const [form, setForm] = useState(null);
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ email: "", username: "", password: "", name: "", role: "staff" });
  const [newSource, setNewSource] = useState("");
  const fileRef = useRef();

  // Fonnte
  const [waStatus, setWaStatus] = useState(null);
  const [waToken, setWaToken] = useState("");
  const [waCC, setWaCC] = useState("62");
  const [waSaving, setWaSaving] = useState(false);
  const [testTarget, setTestTarget] = useState("");
  const [testing, setTesting] = useState(false);

  useEffect(() => { if (settings) setForm(settings); }, [settings]);
  useEffect(() => {
    api.get("/users").then(r => setUsers(r.data));
    api.get("/whatsapp/status").then(r => { setWaStatus(r.data); setWaCC(r.data.country_code || "62"); });
  }, []);

  if (!form) return <div>Memuat...</div>;

  const save = async () => {
    try {
      const { id, ...payload } = form;
      await api.put("/settings", payload);
      toast.success("Pengaturan tersimpan");
      reload();
    } catch (err) {
      const msg = err.response?.data?.detail
        || (Array.isArray(err.response?.data) ? err.response.data.map(x => x.msg).join(", ") : null)
        || err.message
        || "Gagal simpan";
      toast.error(typeof msg === "string" ? msg : "Gagal simpan (cek konsol)");
      console.error("PUT /settings error:", err.response?.data || err);
    }
  };

  const onLogo = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setForm(prev => ({ ...prev, logo_base64: reader.result }));
    reader.readAsDataURL(f);
  };

  const addUser = async () => {
    try {
      await api.post("/users", newUser);
      toast.success("Pengguna ditambahkan");
      setNewUser({ email: "", username: "", password: "", name: "", role: "staff" });
      api.get("/users").then(r => setUsers(r.data));
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal"); }
  };

  const delUser = async (id) => {
    if (!window.confirm("Hapus pengguna?")) return;
    await api.delete(`/users/${id}`);
    api.get("/users").then(r => setUsers(r.data));
  };

  const saveFonnte = async () => {
    if (!waToken.trim()) return toast.error("Token wajib diisi");
    setWaSaving(true);
    try {
      await api.put("/settings/fonnte", { fonnte_token: waToken.trim(), fonnte_country_code: waCC.trim() || "62" });
      toast.success("Fonnte token tersimpan & aktif");
      setWaToken("");
      const r = await api.get("/whatsapp/status");
      setWaStatus(r.data);
    } catch { toast.error("Gagal simpan"); }
    setWaSaving(false);
  };

  const disableFonnte = async () => {
    if (!window.confirm("Nonaktifkan Fonnte? Pengingat WhatsApp kembali ke mode simulasi.")) return;
    await api.put("/settings/fonnte", { fonnte_token: "", fonnte_country_code: waCC.trim() || "62" });
    toast.success("Fonnte dinonaktifkan");
    const r = await api.get("/whatsapp/status");
    setWaStatus(r.data);
  };

  const testFonnte = async () => {
    if (!testTarget.trim()) return toast.error("Isi nomor tujuan uji");
    setTesting(true);
    try {
      const r = await api.post("/whatsapp/test", {
        target: testTarget.trim(),
        message: `[TEST] Halo, pengingat WhatsApp dari ${form.business_name} sudah aktif. Pesan uji dari sistem POS.`
      });
      if (r.data.mocked) toast.info("Mode simulasi (token belum tersimpan).");
      else if (r.data.status === true) toast.success("Pesan uji terkirim ke Fonnte");
      else toast.error("Gagal: " + (r.data.reason || r.data.error || "unknown"));
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal test"); }
    setTesting(false);
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold">Pengaturan</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold text-neutral-900 mt-1">Konfigurasi Sistem</h1>
      </div>

      <Tabs defaultValue="brand">
        <TabsList className="overflow-x-auto flex">
          <TabsTrigger value="brand" data-testid="tab-brand"><Store className="w-4 h-4 mr-1" /> Brand</TabsTrigger>
          <TabsTrigger value="theme" data-testid="tab-theme"><Palette className="w-4 h-4 mr-1" /> Tema</TabsTrigger>
          <TabsTrigger value="receipt" data-testid="tab-receipt"><RcpIcon className="w-4 h-4 mr-1" /> Struk</TabsTrigger>
          <TabsTrigger value="booking" data-testid="tab-booking"><Calendar className="w-4 h-4 mr-1" /> Booking</TabsTrigger>
          <TabsTrigger value="whatsapp" data-testid="tab-whatsapp"><MessageCircle className="w-4 h-4 mr-1" /> WhatsApp</TabsTrigger>
          <TabsTrigger value="reward" data-testid="tab-reward"><Gift className="w-4 h-4 mr-1" /> Referral</TabsTrigger>
          <TabsTrigger value="denda" data-testid="tab-denda"><Clock className="w-4 h-4 mr-1" /> Denda &amp; Jaminan</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users"><UsersIcon className="w-4 h-4 mr-1" /> Pengguna</TabsTrigger>
        </TabsList>

        <TabsContent value="brand">
          <Card className="border-neutral-200 shadow-none">
            <CardContent className="p-6 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div><Label>Nama Toko</Label><Input data-testid="biz-name" value={form.business_name} onChange={e => setForm({...form, business_name: e.target.value})} /></div>
                <div><Label>No. WhatsApp Toko</Label><Input data-testid="biz-phone" value={form.business_phone} onChange={e => setForm({...form, business_phone: e.target.value})} /></div>
                <div className="md:col-span-2"><Label>Alamat Toko</Label><Textarea data-testid="biz-address" value={form.business_address} onChange={e => setForm({...form, business_address: e.target.value})} /></div>
                <div className="md:col-span-2"><Label>Media Sosial</Label><Input value={form.business_socials} onChange={e => setForm({...form, business_socials: e.target.value})} /></div>
                <div className="md:col-span-2"><Label>Logo Toko (dasbor, struk, favicon)</Label>
                  <div className="flex items-center gap-3 mt-1">
                    <input type="file" accept="image/png,image/jpeg" ref={fileRef} onChange={onLogo} className="hidden" data-testid="logo-input" />
                    <Button type="button" variant="outline" onClick={() => fileRef.current?.click()}><Upload className="w-4 h-4 mr-1" /> Pilih Logo</Button>
                    {form.logo_base64 && <img src={form.logo_base64} alt="logo" className="h-14 border border-neutral-200 rounded" />}
                  </div>
                </div>
              </div>
              <Button data-testid="save-settings" onClick={save} className="text-white" style={{background: "var(--brand-primary)"}}>Simpan</Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="theme">
          <Card className="border-neutral-200 shadow-none">
            <CardContent className="p-6 space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label>Warna Utama (Primary)</Label>
                  <div className="flex items-center gap-3 mt-1">
                    <input data-testid="primary-color" type="color" value={form.primary_color} onChange={e => setForm({...form, primary_color: e.target.value})} className="w-14 h-11 border border-neutral-200 rounded" />
                    <Input value={form.primary_color} onChange={e => setForm({...form, primary_color: e.target.value})} className="font-mono-tab" />
                  </div>
                </div>
                <div>
                  <Label>Warna Sekunder</Label>
                  <div className="flex items-center gap-3 mt-1">
                    <input data-testid="secondary-color" type="color" value={form.secondary_color} onChange={e => setForm({...form, secondary_color: e.target.value})} className="w-14 h-11 border border-neutral-200 rounded" />
                    <Input value={form.secondary_color} onChange={e => setForm({...form, secondary_color: e.target.value})} className="font-mono-tab" />
                  </div>
                </div>
              </div>
              <div className="p-6 rounded-md border border-neutral-200">
                <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-3">Pratinjau</div>
                <div className="flex items-center gap-3">
                  <button className="px-4 py-2 rounded-md text-white font-semibold text-sm" style={{background: form.primary_color}}>Primary CTA</button>
                  <button className="px-4 py-2 rounded-md text-white font-semibold text-sm" style={{background: form.secondary_color}}>Secondary</button>
                </div>
              </div>
              <Button data-testid="save-theme" onClick={save} className="text-white" style={{background: "var(--brand-primary)"}}>Terapkan Tema</Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="receipt">
          <Card className="border-neutral-200 shadow-none">
            <CardContent className="p-6 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div><Label>Pesan Header Tambahan</Label>
                  <Input data-testid="rcp-header" value={form.receipt_header_extra || ""} onChange={e => setForm({...form, receipt_header_extra: e.target.value})} placeholder="contoh: PERSEWAAN ALAT CAMPING" /></div>
                <div><Label>Pesan Footer</Label>
                  <Input data-testid="rcp-footer" value={form.receipt_footer || ""} onChange={e => setForm({...form, receipt_footer: e.target.value})} /></div>
                <div className="md:col-span-2"><Label>Ucapan Terima Kasih</Label>
                  <Input data-testid="rcp-thanks" value={form.receipt_thank_you || ""} onChange={e => setForm({...form, receipt_thank_you: e.target.value})} /></div>
              </div>
              <div className="space-y-3 pt-2">
                <div className="flex items-center justify-between p-3 border border-neutral-200 rounded">
                  <div><div className="font-medium text-sm">Tampilkan Logo</div><div className="text-xs text-neutral-500">Cetak logo di atas struk</div></div>
                  <Switch checked={form.receipt_show_logo !== false} onCheckedChange={v => setForm({...form, receipt_show_logo: v})} data-testid="rcp-show-logo" />
                </div>
                <div className="flex items-center justify-between p-3 border border-neutral-200 rounded">
                  <div><div className="font-medium text-sm">Tampilkan QR Survei</div><div className="text-xs text-neutral-500">Cetak QR code untuk survei kepuasan</div></div>
                  <Switch checked={form.receipt_show_qr !== false} onCheckedChange={v => setForm({...form, receipt_show_qr: v})} data-testid="rcp-show-qr" />
                </div>
                <div className="flex items-center justify-between p-3 border border-neutral-200 rounded">
                  <div><div className="font-medium text-sm">Tampilkan Media Sosial</div><div className="text-xs text-neutral-500">Cetak handle sosmed di header</div></div>
                  <Switch checked={form.receipt_show_socials !== false} onCheckedChange={v => setForm({...form, receipt_show_socials: v})} data-testid="rcp-show-socials" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4 pt-4 border-t border-neutral-200">
                <div><Label>Ukuran TOTAL di Struk</Label>
                  <Select value={form.receipt_total_size || "sedang"} onValueChange={v => setForm({...form, receipt_total_size: v})}>
                    <SelectTrigger data-testid="rcp-total-size"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="kecil">Kecil</SelectItem>
                      <SelectItem value="sedang">Sedang</SelectItem>
                      <SelectItem value="besar">Besar</SelectItem>
                    </SelectContent>
                  </Select></div>
                <div><Label>Lebar Kertas Default</Label>
                  <Select value={String(form.receipt_paper_width || 32)} onValueChange={v => setForm({...form, receipt_paper_width: Number(v)})}>
                    <SelectTrigger data-testid="rcp-paper-width"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="32">58mm (32 karakter)</SelectItem>
                      <SelectItem value="48">80mm (48 karakter)</SelectItem>
                    </SelectContent>
                  </Select></div>
              </div>
              <div className="space-y-3">
                {[
                  ["receipt_show_kasir", "Tampilkan Kasir"],
                  ["receipt_show_customer", "Tampilkan Nama & WA Pelanggan"],
                  ["receipt_show_pickup_time", "Tampilkan Jam Ambil Barang"],
                  ["receipt_show_deposit", "Tampilkan Jaminan"],
                  ["receipt_show_notes", "Tampilkan Catatan Transaksi"],
                ].map(([k, label]) => (
                  <div key={k} className="flex items-center justify-between p-3 border border-neutral-200 rounded">
                    <div className="font-medium text-sm">{label}</div>
                    <Switch checked={k === "receipt_show_notes" ? !!form[k] : form[k] !== false} onCheckedChange={v => setForm({...form, [k]: v})} data-testid={`sw-${k}`} />
                  </div>
                ))}
              </div>
              <div><Label>Catatan Kustom Struk (mis. jam operasional / promo)</Label>
                <Textarea data-testid="rcp-custom-note" value={form.receipt_custom_note || ""} onChange={e => setForm({...form, receipt_custom_note: e.target.value})} placeholder="Tampil di atas footer struk" /></div>
              <Button data-testid="save-receipt" onClick={save} className="text-white" style={{background: "var(--brand-primary)"}}>Simpan Kustomisasi</Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="booking">
          <Card className="border-neutral-200 shadow-none">
            <CardContent className="p-6 space-y-4 max-w-lg">
              <div>
                <Label>Rentang "Siapkan" (H-berapa)</Label>
                <Input data-testid="prep-days" type="number" min="0" max="30" value={form.booking_prep_days ?? 2} onChange={e => setForm({...form, booking_prep_days: Number(e.target.value)})} />
                <div className="mt-3"><Label>Batas Diskon Maks. Staf (%)</Label>
                  <Input data-testid="max-staff-discount" type="number" min="0" max="100" value={form.max_staff_discount_percent ?? 100} onChange={e => setForm({...form, max_staff_discount_percent: Number(e.target.value)})} />
                  <p className="text-xs text-neutral-500 mt-1">Owner tanpa batas. Staf tidak bisa memberi diskon melebihi nilai ini.</p></div>
                <p className="text-xs text-neutral-500 mt-1">
                  Tentukan berapa hari sebelum pickup booking akan muncul di tab <b>"Siapkan Sekarang"</b>.<br/>
                  Contoh: <b>2</b> = tampilkan booking yang akan diambil hari ini, besok, dan lusa (H-0 sampai H-2).
                </p>
              </div>
              <div className="border-t border-neutral-200 pt-4">
                <Label>Opsi "Tahu {form.business_name} dari mana?"</Label>
                <p className="text-xs text-neutral-500 mt-1 mb-2">Pilihan sumber pelanggan pada form booking publik & kasir. Tambah/hapus sesuai kebutuhan.</p>
                <div className="flex flex-wrap gap-2 mb-2" data-testid="source-chips">
                  {(form.acquisition_sources || []).map((s, i) => (
                    <span key={s} className="flex items-center gap-1 text-xs px-2 py-1 rounded-full border border-neutral-200 bg-neutral-50" data-testid={`source-chip-${i}`}>
                      {s}
                      <button type="button" onClick={() => setForm({...form, acquisition_sources: form.acquisition_sources.filter(x => x !== s)})} className="text-red-600" data-testid={`source-del-${i}`}><XCircle className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input data-testid="new-source-input" placeholder="Sumber baru, mis. Shopee" value={newSource} onChange={e => setNewSource(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); const v = newSource.trim(); if (v && !(form.acquisition_sources||[]).includes(v)) setForm({...form, acquisition_sources: [...(form.acquisition_sources||[]), v]}); setNewSource(""); } }} />
                  <Button type="button" variant="outline" data-testid="new-source-add" onClick={() => { const v = newSource.trim(); if (v && !(form.acquisition_sources||[]).includes(v)) setForm({...form, acquisition_sources: [...(form.acquisition_sources||[]), v]}); setNewSource(""); }}><Plus className="w-4 h-4" /></Button>
                </div>
              </div>
              <Button data-testid="save-booking" onClick={save} className="text-white" style={{background: "var(--brand-primary)"}}>Simpan</Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="whatsapp">
          <Card className="border-neutral-200 shadow-none">
            <CardContent className="p-6 space-y-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-display font-semibold text-lg flex items-center gap-2">
                    Fonnte WhatsApp Gateway
                    {waStatus?.active
                      ? <span className="text-xs font-bold uppercase px-2 py-1 rounded bg-green-50 text-green-700 border border-green-200 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Aktif</span>
                      : <span className="text-xs font-bold uppercase px-2 py-1 rounded bg-neutral-100 text-neutral-600 border border-neutral-200 flex items-center gap-1"><XCircle className="w-3 h-3" /> Simulasi</span>}
                  </h3>
                  <p className="text-sm text-neutral-500 mt-1">Aktifkan agar pengingat H-1 pukul 09:00 WITA benar-benar terkirim ke pelanggan.</p>
                  {waStatus?.active && <p className="text-xs text-neutral-500 mt-1 font-mono-tab">Token tersimpan: {waStatus.masked_token} · Sumber: {waStatus.source}</p>}
                </div>
              </div>

              <div className="bg-neutral-50 border border-neutral-200 rounded-md p-4 text-sm space-y-2">
                <div className="font-semibold text-neutral-900">Cara mendapatkan token Fonnte:</div>
                <ol className="list-decimal ml-5 space-y-1 text-neutral-700">
                  <li>Login ke <a href="https://md.fonnte.com/" target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-1" style={{color: "var(--brand-primary)"}}>md.fonnte.com <ExternalLink className="w-3 h-3" /></a></li>
                  <li>Buat / pilih <b>Device</b>, hubungkan nomor WhatsApp (scan QR)</li>
                  <li>Buka menu <b>Device → Device list</b>, salin token</li>
                  <li>Pastikan status device <b>"connect"</b></li>
                </ol>
              </div>

              <div className="grid md:grid-cols-3 gap-3">
                <div className="md:col-span-2">
                  <Label>Fonnte Token</Label>
                  <Input data-testid="wa-token" type="password" value={waToken} onChange={e => setWaToken(e.target.value)} placeholder={waStatus?.active ? "Kosongkan untuk tetap pakai token yang ada" : "Paste token dari dashboard Fonnte"} />
                </div>
                <div>
                  <Label>Kode Negara</Label>
                  <Input data-testid="wa-cc" value={waCC} onChange={e => setWaCC(e.target.value)} placeholder="62" />
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button data-testid="wa-save" onClick={saveFonnte} disabled={waSaving} className="text-white" style={{background: "var(--brand-primary)"}}>
                  {waSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan & Aktifkan"}
                </Button>
                {waStatus?.active && <Button variant="outline" onClick={disableFonnte} className="text-red-600 hover:bg-red-50">Nonaktifkan</Button>}
              </div>

              <div className="border-t border-neutral-200 pt-5">
                <Label className="font-semibold text-neutral-900">Uji Kirim Pesan</Label>
                <p className="text-xs text-neutral-500 mt-0.5">Kirim pesan uji ke nomor WhatsApp Anda untuk verifikasi. Butuh token aktif.</p>
                <div className="flex gap-2 mt-2">
                  <Input data-testid="wa-test-target" placeholder="08xxxxxxxxxx atau 628xxxxxxxxxx" value={testTarget} onChange={e => setTestTarget(e.target.value)} />
                  <Button data-testid="wa-test-btn" onClick={testFonnte} disabled={testing || !waStatus?.active} variant="outline">
                    {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Kirim Uji"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="reward">
          <Card className="border-neutral-200 shadow-none">
            <CardContent className="p-6 space-y-4 max-w-lg">
              <div><Label>Tipe Reward</Label>
                <Select value={form.reward_type} onValueChange={v => setForm({...form, reward_type: v})}>
                  <SelectTrigger data-testid="reward-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="poin">Opsi A: Poin (bisa ditukar voucher sewa)</SelectItem>
                    <SelectItem value="komisi">Opsi B: Komisi Sewa (%)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>{form.reward_type === "poin" ? "Poin per Referral" : "Persentase Komisi (%)"}</Label>
                <Input data-testid="reward-value" type="number" value={form.reward_value} onChange={e => setForm({...form, reward_value: Number(e.target.value)})} />
              </div>
              <Button data-testid="save-reward" onClick={save} className="text-white" style={{background: "var(--brand-primary)"}}>Simpan</Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="denda">
          <Card className="border-neutral-200 shadow-none">
            <CardContent className="p-6 space-y-4 max-w-lg">
              <div className="flex items-center justify-between p-3 border border-neutral-200 rounded">
                <div><div className="font-medium text-sm">Aktifkan Denda Keterlambatan</div><div className="text-xs text-neutral-500">Hitung denda saat barang telat dikembalikan</div></div>
                <Switch checked={!!form.late_fee_enabled} onCheckedChange={v => setForm({...form, late_fee_enabled: v})} data-testid="late-enabled" />
              </div>
              {form.late_fee_enabled && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Mode Denda</Label>
                      <Select value={form.late_fee_mode || "manual"} onValueChange={v => setForm({...form, late_fee_mode: v})}>
                        <SelectTrigger data-testid="late-mode"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="auto">Otomatis</SelectItem>
                          <SelectItem value="manual">Manual (bisa diubah)</SelectItem>
                        </SelectContent>
                      </Select></div>
                    <div><Label>Jenis Denda</Label>
                      <Select value={form.late_fee_type || "per_jam"} onValueChange={v => setForm({...form, late_fee_type: v})}>
                        <SelectTrigger data-testid="late-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="per_jam">Per Jam (Rp)</SelectItem>
                          <SelectItem value="per_hari">Per Hari (tambahan hari sewa)</SelectItem>
                          <SelectItem value="persen">Persen Bertingkat (%)</SelectItem>
                        </SelectContent>
                      </Select></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Denda Mulai (hari)</Label>
                      <Select value={String(form.late_fee_start_day_offset ?? 0)} onValueChange={v => setForm({...form, late_fee_start_day_offset: Number(v)})}>
                        <SelectTrigger data-testid="late-day-offset"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0">Di hari kembali seharusnya</SelectItem>
                          <SelectItem value="1">1 hari setelah hari kembali</SelectItem>
                          <SelectItem value="2">2 hari setelah hari kembali</SelectItem>
                          <SelectItem value="3">3 hari setelah hari kembali</SelectItem>
                        </SelectContent>
                      </Select></div>
                    <div><Label className="text-xs">Mulai Pukul (jam)</Label><Input type="number" min="0" max="23" data-testid="late-start-hour" value={form.late_fee_start_hour ?? 12} onChange={e => setForm({...form, late_fee_start_hour: Number(e.target.value)})} /></div>
                  </div>

                  {form.late_fee_type === "per_jam" && (
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label className="text-xs">Denda / Jam (Rp)</Label><Input type="number" min="0" data-testid="late-per-hour" value={form.late_fee_per_hour ?? 0} onChange={e => setForm({...form, late_fee_per_hour: Number(e.target.value)})} /></div>
                      <div><Label className="text-xs">Ambang → +1 Hari (jam)</Label><Input type="number" min="0" data-testid="late-full-day" value={form.late_fee_full_day_hours ?? 6} onChange={e => setForm({...form, late_fee_full_day_hours: Number(e.target.value)})} /></div>
                    </div>
                  )}
                  {form.late_fee_type === "per_hari" && (
                    <p className="text-xs text-neutral-500">Setiap kelipatan 24 jam keterlambatan dihitung sebagai <b>+1 hari sewa penuh</b> (harga sewa/hari × jumlah alat).</p>
                  )}
                  {form.late_fee_type === "persen" && (
                    <div className="space-y-2 border border-neutral-200 rounded p-3">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs">Denda Bertingkat (berdasarkan jam)</Label>
                        <Select value={form.late_fee_percent_base || "total"} onValueChange={v => setForm({...form, late_fee_percent_base: v})}>
                          <SelectTrigger className="h-8 w-40" data-testid="late-percent-base"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="total">% dari Total</SelectItem>
                            <SelectItem value="subtotal">% dari Subtotal</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {(form.late_fee_tiers || []).map((t, i) => (
                        <div key={i} className="flex items-center gap-2" data-testid={`late-tier-${i}`}>
                          <span className="text-xs text-neutral-500">Sampai pukul</span>
                          <Input type="number" min="0" max="23" className="h-8 w-20" value={t.hour} onChange={e => { const arr=[...form.late_fee_tiers]; arr[i]={...arr[i],hour:Number(e.target.value)}; setForm({...form,late_fee_tiers:arr}); }} />
                          <span className="text-xs text-neutral-500">→ denda</span>
                          <Input type="number" min="0" className="h-8 w-20" value={t.percent} onChange={e => { const arr=[...form.late_fee_tiers]; arr[i]={...arr[i],percent:Number(e.target.value)}; setForm({...form,late_fee_tiers:arr}); }} />
                          <span className="text-xs text-neutral-500">%</span>
                          <Button type="button" variant="ghost" size="sm" className="text-red-600 h-8 w-8 p-0" onClick={() => setForm({...form, late_fee_tiers: form.late_fee_tiers.filter((_,x)=>x!==i)})} data-testid={`late-tier-del-${i}`}><XCircle className="w-4 h-4" /></Button>
                        </div>
                      ))}
                      <Button type="button" variant="outline" size="sm" onClick={() => setForm({...form, late_fee_tiers: [...(form.late_fee_tiers||[]), {hour: 12, percent: 20}]})} data-testid="late-tier-add"><Plus className="w-3 h-3 mr-1" /> Tambah Tingkat</Button>
                      <div><Label className="text-xs">Tambahan % per hari berikutnya</Label><Input type="number" min="0" className="h-8" data-testid="late-percent-extra-day" value={form.late_fee_percent_extra_day ?? 0} onChange={e => setForm({...form, late_fee_percent_extra_day: Number(e.target.value)})} /></div>
                      <p className="text-[11px] text-neutral-500">Contoh: sampai pukul 12 → 20%, sampai pukul 15 → 40%. Lewat tingkat terakhir, ditambah {form.late_fee_percent_extra_day ?? 0}% tiap hari berikutnya.</p>
                    </div>
                  )}
                </>
              )}
              <div className="border-t border-neutral-200 pt-4">
                <Label>Teks Pop-up "Minta Jaminan" (POS)</Label>
                <Textarea data-testid="deposit-reminder-text-input" value={form.deposit_reminder_text || ""} onChange={e => setForm({...form, deposit_reminder_text: e.target.value})} placeholder="Jangan lupa minta jaminan/KTP dari pelanggan sebelum barang dibawa." />
                <p className="text-xs text-neutral-500 mt-1">Muncul saat kasir menekan "Simpan &amp; Cetak Struk".</p>
              </div>
              <Button data-testid="save-denda" onClick={save} className="text-white" style={{background: "var(--brand-primary)"}}>Simpan</Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="users">
          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="border-neutral-200 shadow-none">
              <CardContent className="p-6">
                <h3 className="font-display font-semibold text-lg mb-3">Daftar Pengguna</h3>
                <div className="space-y-3">
                  {users.map(u => (
                    <div key={u.id} className="border border-neutral-200 rounded p-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">{u.name}</div>
                          <div className="text-xs text-neutral-500">{u.email}{u.username && <span className="ml-1 font-mono-tab text-neutral-700">· @{u.username}</span>}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] uppercase font-bold px-2 py-1 rounded" style={{background: u.role === "owner" ? "#FEE2E2" : "#F5F5F5", color: u.role === "owner" ? "#B91C1C" : "#525252"}}>{u.role}</span>
                          <Button variant="ghost" size="sm" onClick={() => delUser(u.id)} className="text-red-600"><Trash2 className="w-3 h-3" /></Button>
                        </div>
                      </div>
                      {u.role === "staff" && (
                        <div className="mt-3 pt-3 border-t border-neutral-100">
                          <div className="text-xs uppercase tracking-wider text-neutral-500 font-semibold mb-2">Hak Akses Menu</div>
                          <div className="grid grid-cols-2 gap-2">
                            {ALL_PERMS.map(p => {
                              const checked = (u.permissions || []).includes(p);
                              return (
                                <label key={p} className="flex items-center gap-2 text-xs cursor-pointer">
                                  <input type="checkbox" checked={checked} onChange={async (e) => {
                                    const newPerms = e.target.checked
                                      ? [...(u.permissions||[]), p]
                                      : (u.permissions||[]).filter(x => x !== p);
                                    try {
                                      await api.put(`/users/${u.id}/permissions`, { permissions: newPerms });
                                      toast.success("Akses diperbarui");
                                      api.get("/users").then(r => setUsers(r.data));
                                    } catch { toast.error("Gagal"); }
                                  }} data-testid={`perm-${u.id}-${p}`} className="rounded" />
                                  <span className="capitalize">{PERM_LABEL[p] || p}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            <Card className="border-neutral-200 shadow-none">
              <CardContent className="p-6 space-y-3">
                <h3 className="font-display font-semibold text-lg">Tambah Pengguna</h3>
                <div><Label>Nama</Label><Input data-testid="new-user-name" value={newUser.name} onChange={e => setNewUser({...newUser, name: e.target.value})} /></div>
                <div><Label>Email</Label><Input data-testid="new-user-email" type="email" value={newUser.email} onChange={e => setNewUser({...newUser, email: e.target.value})} /></div>
                <div><Label>Username (untuk login)</Label><Input data-testid="new-user-username" value={newUser.username} onChange={e => setNewUser({...newUser, username: e.target.value.toLowerCase()})} placeholder="kosong = bagian depan email" /></div>
                <div><Label>Password</Label><Input data-testid="new-user-password" type="password" value={newUser.password} onChange={e => setNewUser({...newUser, password: e.target.value})} /></div>
                <div><Label>Peran</Label>
                  <Select value={newUser.role} onValueChange={v => setNewUser({...newUser, role: v})}>
                    <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="staff">Staf / Kasir</SelectItem>
                      <SelectItem value="owner">Owner</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button data-testid="add-user-btn" onClick={addUser} className="text-white w-full" style={{background: "var(--brand-primary)"}}><Plus className="w-4 h-4 mr-1" /> Tambah</Button>
                <div className="pt-4 mt-4 border-t border-neutral-200">
                  <div className="text-xs uppercase tracking-wider text-neutral-500 font-semibold mb-2">Link Booking Publik</div>
                  <div className="flex gap-2">
                    <Input value={`${window.location.origin}/book`} readOnly data-testid="public-link" />
                    <Button variant="outline" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/book`); toast.success("Link disalin"); }}><Copy className="w-4 h-4" /></Button>
                  </div>
                  <p className="text-xs text-neutral-500 mt-2">Bagikan link ini ke pelanggan agar mereka bisa memesan sendiri via web.</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
